import React, { useMemo } from 'react';
import { IBootstrapTableColumnProps } from 'react-bootstrap-table-next';
import { ISigningAppointmentDashboardResponse } from '../../../../types/signingAppointment/ISigningAppointmentDashboardResponse';
import { useListMethodsQuery } from '../../../../API/order/signingAppointment/SigningAppointmentAPI';
import { clientUrls } from '../../../../ui-toolkit/rest/ClientUrlFactory';
import { DisplayPartyPurpose, ePartyPurpose } from '../../../../ui-toolkit/enums/ePartyPurpose';
import {
  DigitalSchedulingStatusBadge,
  NonSigSyncAppointmentBadge,
} from '../../../order/scheduling/Reusable/AppointmentStatusBadge';
import { FormattedAppointmentDateTime } from './FormatDateTime';
import { eTransactionCategory } from '../../../../ui-toolkit/enums/eTransactionType';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faPencil, faSend } from '@fortawesome/pro-regular-svg-icons';
import { Tooltip } from 'ui-toolkit';
import { SignatureSyncProgressionBadge } from 'components/order/scheduling/Reusable/SignatureSyncProgressionBadge';
import { useHasFeatureFlag } from '../../../../selectors/useHasFeatureFlag';
import { SpruceOrderId } from '../../util/spruceOrderIndicator';
import { useGetConfigQuery } from '../../../../API/applicationConfig/ApplicationConfigAPI';
import { Anchor } from '@zillow/constellation';

const determineLabel = (
  transactionCategory: eTransactionCategory = eTransactionCategory.Purchase,
  purpose?: ePartyPurpose
) => {
  if (!purpose) {
    return '-';
  }
  if (transactionCategory === eTransactionCategory.Purchase && purpose === ePartyPurpose.borrower) {
    return DisplayPartyPurpose[ePartyPurpose.buyer];
  }
  return DisplayPartyPurpose[purpose];
};

export function useSigningAppointmentsColumns(): IBootstrapTableColumnProps<ISigningAppointmentDashboardResponse>[] {
  const { data: signingMethods = [] } = useListMethodsQuery();
  const isZillowClosingEnabled = useHasFeatureFlag('pinecone.zillow-closing');
  const { data: config } = useGetConfigQuery();

  const methodMap = useMemo(() => {
    const map: Record<string, string> = {};
    signingMethods.forEach((method) => {
      map[method.id] = method.label;
    });
    return map;
  }, [signingMethods]);

  return [
    {
      dataField: 'fileNumber',
      text: 'Order Id',
      formatter: (_, row) => {
        const useSpruceLinkFromConfig = isZillowClosingEnabled && (row.isSpruce ?? false);
        const href =
          useSpruceLinkFromConfig && config?.spruceInstanceHostname
            ? clientUrls.urlFor.spruceOrderStatus(row.orderId, config.spruceInstanceHostname)
            : clientUrls.urlFor.orderSchedulingByAppointment(row.orderId, row.id);

        return (
          <>
            {!!row.correctionTriggeredById && (
              <Tooltip tooltipContent={'Correction Appointment'} trigger={'hover'} placement={'top'}>
                <FontAwesomeIcon className="mr-2 mt-1" icon={faPencil} />
              </Tooltip>
            )}

            {!!row.isSignatureSyncAppointment && (
              <Tooltip tooltipContent={'Signature Sync Appointment'} trigger={'hover'} placement={'top'}>
                <FontAwesomeIcon className="mr-2 mt-1" icon={faSend} />
              </Tooltip>
            )}

            {!!row.isDigitalScheduling && (
              <Tooltip tooltipContent={'Digital Scheduling Appointment'} trigger={'hover'} placement={'top'}>
                <FontAwesomeIcon className="mr-2 mt-1" icon={faEnvelope} />
              </Tooltip>
            )}

            <Anchor href={href} target={'_blank'} rel={'noreferrer noopener'}>
              <SpruceOrderId fileNumber={row.fileNumber ?? ''} showIcon={useSpruceLinkFromConfig} />
            </Anchor>
          </>
        );
      },
      sort: true,
    },
    {
      dataField: 'signers',
      text: 'Signer',
      formatter: (_, row) => {
        const firstSigner = row.signers?.[0];
        if (!firstSigner) {
          return '-';
        }

        return (
          <div className="flex flex-col">
            <p className="font-semibold">{`${firstSigner.firstName} ${firstSigner.lastName}`}</p>
            <p className="text-gray-500">{determineLabel(row.transactionCategory, firstSigner.person?.purpose)}</p>
          </div>
        );
      },
    },
    {
      dataField: 'signingMethod',
      text: 'Method',
      sort: true,
      formatter: (_, row) => {
        if (!row.signingMethod) {
          return '-';
        }
        return methodMap[row.signingMethod] ?? row.signingMethod;
      },
    },
    {
      dataField: 'status',
      text: 'Status',
      formatter: (_, row) => {
        if (row.isDigitalScheduling && !row.isDigitalSchedulingConfirmed) {
          return <DigitalSchedulingStatusBadge appointment={row} />;
        }

        if (row.isSignatureSyncAppointment) {
          return <SignatureSyncProgressionBadge appointmentId={row.id} appointmentOrderId={row.orderId} />;
        }

        if (!row.status) {
          return '-';
        }

        return <NonSigSyncAppointmentBadge appointment={row} />;
      },
      sort: true,
    },
    {
      dataField: 'scheduledFor',
      text: 'Signing Date and Time',
      formatter: (_, row) => <FormattedAppointmentDateTime row={row} />,
      sort: true,
    },
    {
      dataField: 'closingDate',
      text: 'Closing Date',
      formatter: (_, row) => row.closingDate ?? '-',
      sort: true,
    },
  ];
}
